<template>
  <PageShell active="catalogue">
    <section class="catalogue-page">
      <div class="breadcrumb">
        <span>Genuine Online Car Parts Catalogue</span>
        <span>&gt;</span>
        <button type="button" @click="resetSearch">{{ selectedBrandName || 'All Brands' }}</button>
        <template v-if="selectedCarModelName">
          <span>&gt;</span>
          <span class="muted">{{ selectedCarModelName }}</span>
        </template>
        <template v-else-if="searchKeyword">
          <span>&gt;</span>
          <span class="muted">Search: {{ searchKeyword }}</span>
        </template>
      </div>

      <h1>{{ selectedBrandName || (searched ? 'Search Results' : 'Parts Catalogue') }}</h1>

      <div class="part-search">
        <el-select v-model="selectedBrandId" placeholder="Select Brand" @change="onBrandChange" clearable>
          <el-option v-for="brand in brands" :key="brand.id" :label="brand.name" :value="brand.id"></el-option>
        </el-select>
        <el-select v-model="selectedCarModelId" placeholder="Select Model" @change="onCarModelChange" clearable
          :disabled="!carModels.length">
          <el-option v-for="model in carModels" :key="model.id" :label="model.name" :value="model.id"></el-option>
        </el-select>
        <el-input v-model="keyword" placeholder="Part Number" class="part-search-input"
          @keyup.enter.native="searchParts" />
        <button type="button" @click="searchParts">Find</button>
      </div>

      <div v-if="loading" style="text-align:center;padding:40px 0;color:#999;">Loading...</div>

      <table v-if="searched && !loading" class="parts-table">
        <thead>
          <tr>
            <th>Part Number</th>
            <th>Product Name</th>
            <th>Selling Price</th>
            <th>Vehicle model</th>
            <th>Remark</th>
            <th>Qty</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(part, index) in partsList" :key="index">
            <td class="part-number">{{ part.part_number }}</td>
            <td>{{ part.name }}</td>
            <td>${{ part.price }}</td>
            <td>{{ part.car_model ? part.car_model.name : '-' }}</td>
            <td>-</td>
            <td><el-input v-model.number="part.qty" class="qty-input" type="number" :min="1" @keydown="preventNegative"
                @input="clampQty(part)" /></td>
            <td>
              <button class="cart-btn" type="button" @click="addToCart(part)">
                <img src="../../assets/cart.png" alt="Cart" class="cart-icon">
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="searched && !loading && partsList.length === 0" style="text-align:center;padding:40px 0;color:#999;">
        No results found.
      </div>
    </section>
  </PageShell>
</template>

<script>
import PageShell from '@/components/common/PageShell.vue'
import { getToken } from '@/util/token-util.js'

export default {
  name: 'CataloguePage',
  components: {
    PageShell
  },
  data() {
    return {
      keyword: '',
      brands: [],
      carModels: [],
      partsList: [],
      selectedBrandId: '',
      selectedBrandName: '',
      selectedCarModelId: '',
      loading: false,
      searchKeyword: ''
    }
  },
  computed: {
    searched() {
      return this.partsList.length > 0 || !!this.searchKeyword
    },
    selectedCarModelName() {
      const model = this.carModels.find(m => m.id === this.selectedCarModelId)
      return model ? model.name : ''
    }
  },
  async mounted() {
    await this.loadBrands()
    const { keyword, brand_id } = this.$route.query
    if (keyword) {
      this.keyword = keyword
    }
    if (brand_id) {
      const brand = this.brands.find(b => String(b.id) === String(brand_id))
      if (brand) {
        await this.onBrandChange(brand.id)
        return
      }
    }
    if (this.keyword) {
      this.searchParts()
    }
    this.$root.$on('header-search', this.onHeaderSearch)
  },
  beforeDestroy() {
    this.$root.$off('header-search', this.onHeaderSearch)
  },
  methods: {
    async loadBrands() {
      this.loading = true
      try {
        const res = await this.$api({
          url: 'Catalog/brandList',
          method: 'get'
        })
        this.brands = res.data || []
      } catch (e) {
        this.$message.error('Failed to load brands')
      } finally {
        this.loading = false
      }
    },
    async onBrandChange(brandId) {
      this.selectedCarModelId = ''
      this.carModels = []
      if (!brandId) {
        this.selectedBrandName = ''
      } else {
        const brand = this.brands.find(b => b.id === brandId)
        if (brand) {
          this.selectedBrandId = brand.id
          this.selectedBrandName = brand.name
          this.loading = true
          try {
            const res = await this.$api({
              url: 'Catalog/carModelList',
              method: 'get',
              data: { brand_id: brand.id }
            })
            this.carModels = res.data || []
          } catch (e) {
            this.$message.error('Failed to load car models')
          } finally {
            this.loading = false
          }
        }
      }
      this.searchParts()
    },
    onCarModelChange(carModelId) {
      this.selectedCarModelId = carModelId || ''
      this.searchParts()
    },
    async searchParts() {
      this.loading = true
      this.searchKeyword = this.keyword
      try {
        const params = {}
        if (this.keyword) params.keyword = this.keyword
        if (this.selectedBrandId) params.brand_id = this.selectedBrandId
        if (this.selectedCarModelId) params.car_model_id = this.selectedCarModelId
        const res = await this.$api({
          url: 'Catalog/searchPart',
          method: 'get',
          data: params
        })
        const parts = res.data?.items || []
        this.partsList = parts.map(p => ({ ...p, qty: 1 }))
      } catch (e) {
        this.$message.error('Search failed')
      } finally {
        this.loading = false
      }
    },
    async addToCart(part) {
      if (!getToken()) {
        this.$message.warning('Please log in first')
        this.$router.push({ name: 'home' })
        return
      }
      try {
        await this.$api({
          url: '/Cart/add',
          method: 'post',
          data: { part_id: part.id, quantity: part.qty || 1 }
        })
        this.$message.success('Added to cart')
        this.$root.$emit('cart-updated')
      } catch (e) {
        this.$message.error('Failed to add to cart')
      }
    },
    resetSearch() {
      this.selectedBrandId = ''
      this.selectedBrandName = ''
      this.selectedCarModelId = ''
      this.carModels = []
      this.partsList = []
      this.searchKeyword = ''
      this.keyword = ''
    },
    preventNegative(e) {
      if (e.key === '-' || e.key === 'e' || e.key === 'E') {
        e.preventDefault()
      }
    },
    clampQty(part) {
      if (part.qty < 1 || part.qty === null || part.qty === undefined) {
        part.qty = 1
      }
    },
    onHeaderSearch(keyword) {
      this.keyword = keyword || ''
      this.selectedBrandId = ''
      this.selectedBrandName = ''
      this.selectedCarModelId = ''
      this.carModels = []
      this.searchParts()
    }
  }
}
</script>

<style scoped>
.catalogue-page {
  max-width: 1100px;
  min-height: 690px;
  margin: 0 auto;
  padding: 30px 20px 60px;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #777;
  font-size: 16px;
}

.breadcrumb span:first-child,
.breadcrumb button {
  color: #F34B01;
}

.breadcrumb button {
  border: 0;
  background: transparent;
  cursor: pointer;
  font-size: 16px;
}

.breadcrumb .muted {
  color: #777;
}

h1 {
  margin: 36px 0 24px;
  color: #333;
  font-size: 32px;
  font-weight: 700;
}

.part-search {
  display: flex;
  gap: 8px;
}

.part-search-input {
  width: 270px;
}

.part-search-input ::v-deep .el-input__inner {
  height: 38px;
  border: 1px solid #d6d6d6;
  border-radius: 4px;
  padding: 0 16px;
  color: #333;
  font-size: 16px;
  outline: none;
}

.part-search button {
  width: 80px;
  height: 38px;
  border: 0;
  border-radius: 4px;
  background: #F34B01;
  color: #fff;
  cursor: pointer;
  font-size: 16px;
}

.parts-table {
  width: 1186px;
  margin-top: 58px;
  border-collapse: collapse;
  border: 1px solid #dedede;
  color: #111;
  font-size: 14px;
}

.parts-table th,
.parts-table td {
  height: 44px;
  border: 1px solid #dedede;
  text-align: center;
}

.parts-table th {
  background: #f3f3f3;
  font-weight: 700;
}

.part-number {
  color: #F34B01;
}

.qty-input {
  width: 90px;
}

.qty-input ::v-deep .el-input__inner {
  height: 32px;
  padding-left: 14px;
  color: #111;
  font-size: 15px;
}

.qty-input ::v-deep .el-input__inner:focus {
  border: 1px solid #F34B01;
  border-color: #F34B01;
}

.cart-btn {
  width: 40px;
  height: 24px;
  border: 0;
  background: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.cart-icon {
  width: 70%;
  height: auto;
}

@media (max-width: 980px) {
  .parts-table {
    width: 100%;
  }
}
</style>
